import {Component, OnDestroy, OnInit, computed, effect, inject, Injector, runInInjectionContext, signal} from '@angular/core';
import {Router, RouterOutlet} from '@angular/router';

import {CommandPalette} from './shared/components/command-palette/command-palette';
import {StoreDbService} from './core/db/store.db.service';
import {LoginModalAutorice} from './shared/components/login-modal-autorice/login-modal-autorice';
import {ScreenLockService} from './core/services/screen-lock.service';
import {AppUpdateService} from './shared/services/update-sw.service';

import {ButtonModule} from 'primeng/button';
import {AppUpdateToast} from './shared/components/app-update-toast/app-update-toast';
import {IdleModal} from './shared/components/idle-modal/idle-modal';
import {ThemeConfig} from './core/config/theme.config';
import {ErrorLogDbService} from './core/db/error-log.db.service';
import {PrimeNG} from 'primeng/config';
import {LoadingComponent} from './shared/components/loading/loading.component';
import {NetworkService} from './core/services/network.service';
import {TabManagerService} from './core/services/tab-manager.service';
import {PwaInstallService} from './shared/services/pwa-install.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, CommandPalette, LoginModalAutorice, ButtonModule, AppUpdateToast, IdleModal, LoadingComponent],
  templateUrl: './app.html',
  standalone: true,
  styleUrl: './app.scss'
})
export class App implements OnInit, OnDestroy {
  private readonly pwaInstallService = inject(PwaInstallService);
  private readonly INSTALL_PROMPT_REMIND_AT_KEY = 'pwaInstallPromptRemindAt';
  private readonly LEGACY_INSTALL_PROMPT_REMIND_AT_KEY = 'pwa-install-prompt-remind-at';
  private readonly INSTALL_PROMPT_REMIND_DAYS = 7;
  screenLock = inject(ScreenLockService);
  readonly tabManager = inject(TabManagerService);
  private readonly injector = inject(Injector);
  private readonly router = inject(Router);
  private storeDb: StoreDbService | null = null;
  network: NetworkService | null = null;
  private primaryInitialized = false;
  showInstallPrompt = false;
  isIOSDevice = false;
  canInstall = false;
  installPromptSeconds = signal(0);
  private installPromptTimeout: ReturnType<typeof setInterval> | null = null;

  private readonly onAppInstalled = (): void => {
    this.canInstall = false;
    this.showInstallPrompt = false;
    this.removeInstallPromptReminder();
    this.clearInstallPromptTimeout();
  };

  store = computed(() => this.storeDb?.store() ?? null);
  protected title = 'PuntoVenta';
  theme = computed<ThemeConfig>(() => {
    const store = this.store();
    if (!store) {
      return { background: '/images/backgrounds/backgrounds.jpg' };
    }

    if (store.imagenFondo) {
      return {
        background: `/images/backgrounds/${store.imagenFondo}`
      };
    }

    return {
      background: '/images/backgrounds/backgrounds.jpg'
    };

  });

  ngOnInit() {
    this.removeLegacyInstallPromptReminder();
    this.isIOSDevice = this.isIOS();
    // Android/Chromium muestra el aviso solamente cuando confirma que la PWA
    // se puede instalar. Si ya está instalada, beforeinstallprompt no se emite.
    this.showInstallPrompt = this.isIOSDevice && this.shouldShowInstallPrompt();
    if (this.showInstallPrompt) this.scheduleInstallPromptDismissal();

    window.addEventListener('appinstalled', this.onAppInstalled);
  }

  ngOnDestroy(): void {
    window.removeEventListener('appinstalled', this.onAppInstalled);
    this.clearInstallPromptTimeout();
  }

  async installApp(): Promise<void> {
    await this.pwaInstallService.instalar();
    this.canInstall = false;
    this.showInstallPrompt = false;
    this.clearInstallPromptTimeout();
  }

  dismissInstallPrompt(): void {
    this.showInstallPrompt = false;
    this.clearInstallPromptTimeout();
  }

  remindInstallLater(): void {
    const remindAt = new Date();
    remindAt.setDate(remindAt.getDate() + this.INSTALL_PROMPT_REMIND_DAYS);

    try {
      localStorage.setItem(this.INSTALL_PROMPT_REMIND_AT_KEY, remindAt.toISOString());
    } catch (error) {
      console.warn('No se pudo guardar el recordatorio de instalación.', error);
    }

    this.dismissInstallPrompt();
  }

  private scheduleInstallPromptDismissal(): void {
    this.clearInstallPromptTimeout();
    if (!this.showInstallPrompt) return;

    this.installPromptSeconds.set(5);
    this.installPromptTimeout = setInterval(() => {
      const seconds = this.installPromptSeconds() - 1;
      this.installPromptSeconds.set(seconds);

      if (seconds <= 0) {
        this.showInstallPrompt = false;
        this.clearInstallPromptTimeout();
      }
    }, 1000);
  }

  private clearInstallPromptTimeout(): void {
    if (this.installPromptTimeout) {
      clearInterval(this.installPromptTimeout);
      this.installPromptTimeout = null;
    }
    this.installPromptSeconds.set(0);
  }

  private isIOS(): boolean {
    return /iPad|iPhone|iPod/i.test(navigator.userAgent)
      || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  }

  private isInstalled(): boolean {
    return window.matchMedia('(display-mode: standalone)').matches
      || window.matchMedia('(display-mode: fullscreen)').matches
      || Boolean((navigator as Navigator & {standalone?: boolean}).standalone);
  }

  private shouldShowInstallPrompt(): boolean {
    if (this.isInstalled()) {
      this.removeInstallPromptReminder();
      return false;
    }

    try {
      const storedDate = localStorage.getItem(this.INSTALL_PROMPT_REMIND_AT_KEY);
      if (!storedDate) return true;

      const remindAt = new Date(storedDate).getTime();
      if (Number.isNaN(remindAt)) {
        this.removeInstallPromptReminder();
        return true;
      }

      return Date.now() >= remindAt;
    } catch (error) {
      console.warn('No se pudo consultar el recordatorio de instalación.', error);
      return true;
    }
  }

  private removeInstallPromptReminder(): void {
    try {
      localStorage.removeItem(this.INSTALL_PROMPT_REMIND_AT_KEY);
    } catch {
      // localStorage puede estar bloqueado en algunos modos privados.
    }
  }

  private removeLegacyInstallPromptReminder(): void {
    try {
      localStorage.removeItem(this.LEGACY_INSTALL_PROMPT_REMIND_AT_KEY);
    } catch {
      // localStorage puede estar bloqueado en algunos modos privados.
    }
  }

  constructor() {
    this.tabManager.initialize();

    effect(() => {
      const disponible = this.pwaInstallService.disponibleParaInstalar();

      this.canInstall = disponible;

      if (disponible) {
        this.showInstallPrompt = this.shouldShowInstallPrompt();
        this.scheduleInstallPromptDismissal();
      } else if (!this.isIOSDevice) {
        this.showInstallPrompt = false;
        this.clearInstallPromptTimeout();
      }
    });

    effect(() => {
      if (!this.tabManager.isPrimaryTab() || this.primaryInitialized) {
        return;
      }

      this.initializePrimaryTab();
    });
  }

  private initializePrimaryTab(): void {
    this.primaryInitialized = true;
    this.storeDb = this.injector.get(StoreDbService);
    this.network = this.injector.get(NetworkService);

    this.storeDb.loadInitial();
    runInInjectionContext(this.injector, () => {
      this.injector.get(AppUpdateService).init();
    });
    this.injector.get(ErrorLogDbService).autoCleanIfNeeded();
    this.injector.get(PrimeNG).setTranslation({
      dayNames: ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"],
      dayNamesShort: ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"],
      dayNamesMin: ["Do", "Lu", "Ma", "Mi", "Ju", "Vi", "Sa"],
      monthNames: ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"],
      monthNamesShort: ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"],
      today: 'Hoy',
      clear: 'Limpiar',
      dateFormat: 'dd/mm/yy',
      firstDayOfWeek: 1 // Lunes como primer día
    });
    this.network.checkBackend();
    this.router.initialNavigation();
  }
}
